import { computed, reactive, ref, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { breakpointsTailwind, useBreakpoints, useIntervalFn } from '@vueuse/core';

import type { IReportEmbedConfiguration, Page, Report } from 'powerbi-client';
import { models, service } from 'powerbi-client';
import type { IPaginatedReportLoadConfiguration } from 'powerbi-models';

import { $mxbFetch, $powerBiFetch, $mxsFetch } from '@/utils/fetch';
import type { EventHandler } from 'powerbi-client-vue-js/dist/types/src/utils/utils';

import {
  refreshPowerBiEmbedToken,
  refreshTokenIfNeeded,
  startPowerBiTokenRefreshInterval,
  stopPowerBiTokenRefreshInterval
} from '@/plugins/msal';

import { TokenManager } from '@/utils/token_manager';

import router from '@/router';

import { ofetch } from 'ofetch';
import { useSecurity } from '@/composables/use-security';

import * as Sentry from '@sentry/vue';

export class PowerBiService {
  private tokenType: models.TokenType;

  constructor(tokenType: models.TokenType) {
    this.tokenType = tokenType;
  }

  public async getAccessToken(): Promise<string | null> {
    if (this.tokenType === models.TokenType.Embed) {
      const storedToken = TokenManager.getCachedToken('power_bi_embed_access_token');
      return storedToken?.token || null;
    } else {
      const storedToken = TokenManager.getCachedToken('power_bi_access_token');
      return storedToken?.token || null;
    }
  }

  public fetchAPI = ofetch.create({
    baseURL: 'https://api.powerbi.com/v1.0/myorg',
    onRequest: async ({ options }) => {
      const accessToken = await this.getAccessToken();

      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      };
    }
  });

  public async fetchWorkspaces(): Promise<Array<PowerBIWorkspace>> {
    const result = await this.fetchAPI('/groups').catch(() => []);
    return result?.value || [];
  }

  public async fetchReports(workspace: PowerBIWorkspace): Promise<Array<PowerBiReport>> {
    const result = await this.fetchAPI(`/groups/${workspace.id}/reports`).catch(() => []);
    return (result?.value || []).map((report: PowerBiReport) => ({
      ...report,
      workspace: workspace.name,
      tokenType: this.tokenType // Add tokenType to each report
    }));
  }

  public async fetchPersonalReports(): Promise<Array<PowerBiReport>> {
    const result = await this.fetchAPI('/reports').catch(() => []);
    return (result?.value || []).map((report: PowerBiReport) => ({
      ...report,
      tokenType: this.tokenType,
      workspace: 'Personal'
    }));
  }
}

export class PowerBIReportManager {
  route = useRoute();

  security = useSecurity();

  breakpoints = useBreakpoints(breakpointsTailwind);
  sm = this.breakpoints.smaller('md');

  reportConfig: IReportEmbedConfiguration = reactive({
    type: 'report',
    id: (this.route.params.reportId as string) || '',
    tokenType: !this.route.query?.embedType
      ? models.TokenType.Aad
      : Number(this.route.query.embedType),
    accessToken: '',
    settings: {
      filterPaneEnabled: true,
      persistentFiltersEnabled: this.route.query.embedType === '0' ? true : false,
      visualRenderedEvents: true,
      personalBookmarksEnabled: true,
      panes: {
        fields: {
          expanded: true
        },
        filters: {
          expanded: false,
          visible: true
        },
        pageNavigation: {
          visible: true
        },
        selection: {
          visible: true
        },
        syncSlicers: {
          visible: true
        },
        visualizations: {
          expanded: true
        }
      },
      ...(this.sm.value && {
        layoutType: models.LayoutType.MobilePortrait
      })
    }
  });

  paginatedReportConfig: IPaginatedReportLoadConfiguration = reactive({
    type: 'report',
    id: (this.route.params.reportId as string) || '',
    groupId: (this.route.query.workspaceId as string) || '',
    tokenType: !this.route.query?.embedType
      ? models.TokenType.Aad
      : Number(this.route.query.embedType),
    accessToken: '',
    embedUrl: ''
  });

  isPaginatedReport = this.route.query.reportType === 'PaginatedReport';

  report = ref<Report | null>(null);
  tokenRefreshIntervalActive = ref<boolean>(false);

  activePage = ref<Page>();
  selectedDataObject = ref<Record<string, string[] | string>>({});

  reportDefinition = ref<ReportDefinition['data']>([]);

  // Add visualization readiness tracking
  visualsReady = ref<boolean>(false);
  visualsRenderedCount = ref<number>(0);

  activeViewName = computed<string>(
    () =>
      this.reportDefinition.value?.find(
        ({ pageDisplayName }) => pageDisplayName === this.activePage.value?.displayName
      )?.viewName ?? ''
  );

  // Use VueUse's useIntervalFn for better Vue 3 compatibility
  private tokenRefreshInterval = useIntervalFn(
    async () => {
      if (this.report.value) {
        try {
          await this.refreshToken();
        } catch (error) {
          Sentry.logger.error('Error during scheduled token refresh for report', {
            log_source: 'power_bi',
            reportId: this.reportConfig.id,
            error
          });
        }
      }
    },
    600000, // 10 minutes
    { immediate: false, immediateCallback: false }
  );

  async fetchReportDefinition() {
    const response = await $mxbFetch<ReportDefinition>(
      `/view/definition/report/${this.route.params.reportId}`
    );
    this.reportDefinition.value = response.data;
  }

  async init() {
    // Apply email-based row-level security using report-level filters.
    // This MUST be set BEFORE the accessToken is assigned, because the embed
    // component mounts as soon as accessToken becomes truthy (v-if on accessToken).
    // If filters are applied after the token, the report embeds without them.
    //
    // Note on Filters vs DataPoints:
    // - **Filters** (used here) are applied at the report/page level via the embed configuration.
    //   They restrict the data returned by queries and can appear in the filter pane (unless hidden).
    // - **DataPoints** are selection-based interactions (e.g., clicking a bar in a chart). They
    //   represent user selections on visuals and do NOT appear in the filter panel. DataPoints
    //   work "on top of" filters — they further narrow what is visually highlighted or cross-filtered
    //   within the already-filtered dataset, but they are transient visual interactions, not persistent
    //   query-level restrictions.
    //
    // We use a Basic filter here (rather than datapoint selection) because we need a persistent,
    // query-level restriction that ensures the user only ever sees their own data, regardless of
    // visual interactions.
    const userEmail = this.security.user.value?.email;
    if (userEmail) {
      this.reportConfig.filters = [
        {
          $schema: 'http://powerbi.com/product/schema#basic',
          target: { table: 'users', column: 'email' },
          operator: 'In',
          values: [userEmail] as (string | number | boolean)[],
          filterType: models.FilterType.Basic
        }
      ];
    }

    // Now set the access token — this triggers the embed component to mount.
    const accessToken = await this.setAccessToken();

    if (this.isPaginatedReport) {
      // For paginated reports, look up the embedUrl from cached reports
      const powerBiReports: PowerBiReport[] =
        JSON.parse(localStorage.getItem('power_bi_reports') as string) || [];
      const currentReport = powerBiReports.find(r => r.id === this.route.params.reportId);

      this.paginatedReportConfig.accessToken = accessToken || '';
      this.paginatedReportConfig.embedUrl = currentReport?.embedUrl || '';
    } else {
      this.reportConfig.accessToken = accessToken || '';
    }

    await this.fetchReportDefinition();
    this.startTokenRefresh();

    console.log('Report configuration initialized', {
      log_source: 'power_bi',
      reportId: this.reportConfig.id,
      tokenType: this.reportConfig.tokenType,
      hasEmailFilter: !!userEmail
    });

    // Reset visualization readiness tracking
    this.visualsReady.value = false;
    this.visualsRenderedCount.value = 0;
  }

  private async setAccessToken() {
    await Promise.all([refreshTokenIfNeeded('power_bi_access_token'), refreshPowerBiEmbedToken()]);

    const powerBiReports: PowerBiReport[] =
      JSON.parse(localStorage.getItem('power_bi_reports') as string) || [];

    const reports =
      this.route.params.reportId === '43fa3a4e-ffbc-4d4c-b496-b67d6c700bf9'
        ? Array.from(
            new Map(
              powerBiReports
                .filter(
                  report =>
                    report.webUrl.includes(String(this.route.query.workspaceId ?? '')) && report.id
                )
                .map(report => [report.id, { id: report.id }])
            ).values()
          )
        : [{ id: this.route.params.reportId as string }];

    const datasets = Array.from(
      new Map(
        powerBiReports
          .filter(
            report =>
              report.webUrl.includes(String(this.route.query.workspaceId ?? '')) && report.datasetId
          )
          .map(report => [report.datasetId, { id: report.datasetId }])
      ).values()
    );

    // const failedReports: string[] = [];

    if (Number(this.route.query.embedType) === models.TokenType.Embed) {
      const body: { reports: typeof reports; datasets?: typeof datasets } = { reports };

      if (this.isPaginatedReport) {
        // For paginated reports, fetch datasets from the workspace via Power BI API
        const workspaceId = this.route.query.workspaceId as string;
        if (workspaceId) {
          const datasetsResponse = await $powerBiFetch(`/groups/${workspaceId}/datasets`);
          const workspaceDatasets = (datasetsResponse?.value || []).map((ds: { id: string }) => ({
            id: ds.id,
            xmlaPermissions: 'ReadOnly'
          }));
          body.datasets = workspaceDatasets;
        }
      } else {
        body.datasets = datasets;
      }

      const response = await $powerBiFetch('GenerateToken', {
        method: 'POST',
        body,
        retry: 2,
        retryDelay: 500,
        retryStatusCodes: [400, 403]
      });
      return response.token;
    } else {
      const storedToken = TokenManager.getCachedToken('power_bi_access_token');
      return storedToken?.token || null;
    }
  }

  setReport(report: Report) {
    this.report.value = report;

    // Ensure token refresh is active once report is set
    if (!this.tokenRefreshIntervalActive.value) {
      this.startTokenRefresh();
    }
  }

  // Start token refresh process
  startTokenRefresh() {
    // Start the global token refresh
    startPowerBiTokenRefreshInterval();

    // Start the local token refresh for this specific report
    if (!this.tokenRefreshIntervalActive.value) {
      this.tokenRefreshInterval.resume();
      this.tokenRefreshIntervalActive.value = true;

      // Initial token refresh
      this.refreshToken().catch(error =>
        Sentry.logger.error('Initial Power BI token refresh failed', {
          log_source: 'power_bi',
          reportId: this.reportConfig.id,
          error
        })
      );
    }
  }

  // Clean up resources
  destroy() {
    // Pause the interval when component is destroyed
    this.tokenRefreshInterval.pause();
    this.tokenRefreshIntervalActive.value = false;
    stopPowerBiTokenRefreshInterval();
  }

  // Function to refresh the report
  async refresh() {
    await this.report.value?.refresh();
  }

  // Function to refresh the token in the background
  async refreshToken() {
    try {
      const token = await this.setAccessToken();

      if (this.isPaginatedReport) {
        if (token) {
          this.paginatedReportConfig.accessToken = token;
        }
        return;
      }

      if (token && this.report.value) {
        await this.report.value.setAccessToken(token);
      } else {
        Sentry.logger.warn('Unable to refresh token: token or report is null', {
          log_source: 'power_bi',
          reportId: this.reportConfig.id,
          hasToken: !!token,
          hasReport: !!this.report.value
        });
      }
    } catch (error) {
      Sentry.logger.error('Error refreshing token for report', {
        log_source: 'power_bi',
        reportId: this.reportConfig.id,
        error
      });

      // Log the error to Sentry
      Sentry.withScope(scope => {
        scope.setExtra('reportId', this.reportConfig.id);
        scope.setExtra('tokenType', this.reportConfig.tokenType);
        Sentry.captureException(new Error('Failed to refresh Power BI token for report'));
      });
    }
  }

  async handlePageChange(event?: service.ICustomEvent<any>) {
    this.activePage.value = event?.detail?.newPage || undefined;

    // Reset visual readiness when page changes
    this.visualsReady.value = false;
    this.visualsRenderedCount.value = 0;
  }

  handleDataSelection(event?: service.ICustomEvent<any>) {
    Sentry.logger.info('dataSelected', { log_source: 'power_bi', event });
    const dataPoints = event?.detail?.dataPoints as DataPoint[];
    const newSelection = DataPointMapper.remapDataPoints(dataPoints);
    this.selectedDataObject.value = DataPointMapper.replaceDataPoints(
      this.selectedDataObject.value,
      newSelection
    );
  }

  handleError(event: service.ICustomEvent<any> | undefined) {
    // Check if this is a token-related error
    const errorDetails = event?.detail?.message || '';
    const isTokenError =
      errorDetails.toLowerCase().includes('token') ||
      errorDetails.toLowerCase().includes('auth') ||
      errorDetails.toLowerCase().includes('unauthorized');

    // If it's a token error, try to refresh immediately
    if (isTokenError) {
      Sentry.logger.warn('Token-related error detected, attempting immediate refresh', {
        log_source: 'power_bi',
        reportId: this.reportConfig.id,
        errorDetails
      });
      this.refreshToken().catch(err =>
        Sentry.logger.error('Failed to refresh token after error', {
          log_source: 'power_bi',
          reportId: this.reportConfig.id,
          error: err
        })
      );
    }

    Sentry.withScope(scope => {
      scope.setExtra('event', event);
      scope.setExtra('user', localStorage.getItem('user'));
      scope.setExtra('reportId', this.reportConfig.id);
      Sentry.captureException(new Error('[PBI Event Handler]: API response error'));
    });
    Sentry.logger.error('Error', { log_source: 'power_bi', reportId: this.reportConfig.id, event });
  }

  handleVisualRendered(event?: service.ICustomEvent<any>) {
    Sentry.logger.info('visualRendered', {
      log_source: 'power_bi',
      reportId: this.reportConfig.id,
      event
    });

    // Increment the visual rendered count
    this.visualsRenderedCount.value++;

    // After a threshold of rendered visuals or a timeout, mark visuals as ready
    if (this.visualsRenderedCount.value >= 2) {
      setTimeout(() => {
        Sentry.logger.info('Visuals considered fully rendered', {
          log_source: 'power_bi',
          reportId: this.reportConfig.id,
          visualsRenderedCount: this.visualsRenderedCount.value
        });
        this.visualsReady.value = true;
      }, 1000); // Add a small delay for safety
    }
  }

  eventsHandlers: Map<string, EventHandler> = new Map([
    [
      'pageChanged',
      (event?: service.ICustomEvent<any>) => {
        console.log('pageChanged event fired', { reportId: this.reportConfig.id, event });
        this.handlePageChange(event);
      }
    ],
    [
      'dataSelected',
      (event: service.ICustomEvent<any> | undefined) => {
        console.log('dataSelected event fired', { reportId: this.reportConfig.id, event });
        this.handleDataSelection(event);
      }
    ],
    [
      'loaded',
      async event => {
        console.log('loaded event fired', { reportId: this.reportConfig.id, event });
        Sentry.logger.info('loaded', {
          log_source: 'power_bi',
          reportId: this.reportConfig.id,
          event
        });
        // Reset visual readiness on load
        this.visualsReady.value = false;
        this.visualsRenderedCount.value = 0;

        // Re-apply email filter via report API as a safety net.
        // Even though filters are set in the embed config before mount,
        // re-applying here ensures they persist across reloads/re-embeds.
        const configFilters = this.reportConfig.filters;
        if (this.report.value && Array.isArray(configFilters) && configFilters.length) {
          try {
            await this.report.value.setFilters(configFilters as models.IFilter[]);
            console.log('Email filter applied via report API on load', {
              reportId: this.reportConfig.id
            });
          } catch (err) {
            Sentry.logger.warn('Failed to apply email filter on load', {
              log_source: 'power_bi',
              reportId: this.reportConfig.id,
              error: err
            });
          }
        }

        // Ensure token is fresh on load
        this.refreshToken().catch(err => {
          console.log('Token refresh on load failed', {
            reportId: this.reportConfig.id,
            error: err
          });
          Sentry.logger.error('Token refresh on load failed', {
            log_source: 'power_bi',
            reportId: this.reportConfig.id,
            error: err
          });
        });
      }
    ],
    [
      'visualRendered',
      async event => {
        console.log('visualRendered event fired', {
          reportId: this.reportConfig.id,
          visualsRenderedCount: this.visualsRenderedCount.value,
          event
        });
        this.handleVisualRendered(event);
      }
    ],
    [
      'visualClicked',
      async event => {
        console.log('visualClicked event fired', { reportId: this.reportConfig.id, event });
        Sentry.logger.info('visualClicked', {
          log_source: 'power_bi',
          reportId: this.reportConfig.id,
          event
        });
      }
    ],
    [
      'error',
      async event => {
        console.log('error event fired', { reportId: this.reportConfig.id, event });
        this.handleError(event);
      }
    ]
  ]);

  updateSettings(settings: models.ISettings) {
    this.report.value?.updateSettings(settings);
  }

  isExporting = ref<boolean>(false);

  async captureBookmark(allPages = false) {
    if (!this.report.value) return null;
    return await this.report.value?.bookmarksManager.capture({
      allPages,
      personalizeVisuals: true
    });
  }

  async exportToFile() {
    this.isExporting.value = true;
    const capturedBookmark = await this.captureBookmark();
    if (!capturedBookmark) {
      Sentry.logger.error('Failed to capture bookmark', {
        log_source: 'power_bi',
        reportId: this.reportConfig.id,
        capturedBookmark
      });
    }
    try {
      const response = await $mxsFetch('/power-bi/export-report', {
        method: 'GET',
        responseType: 'blob',
        query: {
          reportId: this.route.params.reportId,
          datasetId: this.route.query.datasetId,
          workspaceId: this.route.query.workspaceId,
          format: 'PDF',
          ...(this.activePage.value?.name ? { pageName: this.activePage.value?.name } : {}),
          ...(capturedBookmark?.state ? { capturedBookmark: capturedBookmark.state } : {})
        }
      });

      // code to download the stream
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${this.route.query.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      Sentry.logger.error('Download failed', {
        log_source: 'power_bi',
        reportId: this.reportConfig.id,
        error: err
      });
    } finally {
      this.isExporting.value = false;
    }
  }
}

// 2. DataPointMapper class for remapping data points
class DataPointMapper {
  static remapDataPoints(dataPoints: DataPoint[]): Record<string, string[] | string> {
    return dataPoints.reduce(
      (acc, dataPoint) => {
        dataPoint.identity.forEach(identity => {
          const key = identity.target.column;
          const value = identity.equals;
          acc[key] = acc[key] ? [...(acc[key] as string[]), value] : [value];
        });
        return acc;
      },
      {} as Record<string, string[] | string>
    );
  }

  static replaceDataPoints(
    existing: Record<string, string[] | string> | undefined,
    newData: Record<string, string[] | string>
  ): Record<string, string[] | string> {
    if (!existing || Object.keys(existing).length === 0) return { ...newData };
    for (const key in newData) {
      if (newData[key] !== undefined) {
        existing[key] = newData[key]!;
      }
    }
    return existing;
  }
}

// 3. DateRangeFormatter class for handling date/time formatting
class DateRangeFormatter {
  static isValidDateTime(dateTimeString: string): boolean {
    const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    return dateTimeRegex.test(dateTimeString);
  }

  static formatDateToMMDDYYYY(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  static convertDateFormat(dateTimeString: string): string {
    if (!this.isValidDateTime(dateTimeString)) return dateTimeString;
    const datePart = dateTimeString.split(' ')[0];
    if (!datePart) return dateTimeString;
    const [year, month, day] = datePart.split('-');
    return `${month}-${day}-${year}`;
  }
}

function getFormattedDate(
  primaryDate?: string | null,
  fallbackDate?: string | null,
  defaultDate?: Date
): string {
  if (primaryDate) {
    return DateRangeFormatter.formatDateToMMDDYYYY(new Date(primaryDate));
  }
  if (fallbackDate) {
    return DateRangeFormatter.convertDateFormat(fallbackDate);
  }
  return defaultDate ? DateRangeFormatter.formatDateToMMDDYYYY(defaultDate) : '';
}

// 4. FilterManager class to manage and retrieve filters
export class FilterManager {
  private filters: Ref<{ column: string; values: any[] }[]> = ref([]);

  private report: PowerBIReportManager;
  private checkCount = 0;
  private waitingPromise: Promise<void> | null = null;

  referenceDateRange = ref<{
    formatted_from_date: string;
    formatted_to_date: string;
  }>({
    formatted_from_date: DateRangeFormatter.formatDateToMMDDYYYY(
      new Date(new Date().setFullYear(new Date().getFullYear() - 10))
    ),
    formatted_to_date: DateRangeFormatter.formatDateToMMDDYYYY(new Date())
  });

  constructor(report: PowerBIReportManager) {
    this.report = report;
  }

  // Wait for visuals to be ready with timeout
  private async waitForVisualsReady(timeoutMs = 5000): Promise<void> {
    if (this.report.visualsReady.value) {
      return Promise.resolve();
    }

    if (this.waitingPromise) {
      return this.waitingPromise;
    }

    this.waitingPromise = new Promise<void>(resolve => {
      const checkInterval = 500; // Check every 500ms
      const maxAttempts = Math.ceil(timeoutMs / checkInterval);
      let attempts = 0;

      const checkReadiness = () => {
        if (this.report.visualsReady.value) {
          this.waitingPromise = null;
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkReadiness, checkInterval);
        } else {
          Sentry.logger.warn('Timed out waiting for visuals to initialize', {
            log_source: 'power_bi',
            reportId: this.report.reportConfig.id,
            attempts,
            maxAttempts
          });
          this.waitingPromise = null;
          resolve(); // Continue anyway after timeout
        }
      };

      checkReadiness();
    });

    return this.waitingPromise;
  }

  // Method to get export data from slicers
  private async getExportDataFromVisuals(visuals: any[]): Promise<any[]> {
    try {
      const exportDataPromises = visuals
        .filter(v => v.type === 'slicer')
        .map(async v => {
          try {
            return await v.exportData(models.ExportDataType.Summarized);
          } catch (error) {
            Sentry.logger.warn('Could not export data for slicer visual', {
              log_source: 'power_bi',
              error
            });
            return { data: '' };
          }
        });
      return await Promise.all(exportDataPromises);
    } catch (error) {
      Sentry.logger.error('Error retrieving export data', { log_source: 'power_bi', error });
      return [];
    }
  }

  // Method to extract the date range from slicers or export data
  private extractDateRange(visualsSlicers: any[], exportData: any[]): void {
    try {
      const dateRangeSlicer = visualsSlicers.find(s =>
        s.targets?.some((st: any) => st?.column === 'reference_date')
      );

      let dateRangeSlicerFromDate = '';
      let dateRangeSlicerToDate = '';

      if (dateRangeSlicer && dateRangeSlicer.filters) {
        const referenceDateFilter = dateRangeSlicer.filters.find(
          (f: any) => f.target?.column === 'reference_date'
        );
        if (referenceDateFilter) {
          dateRangeSlicerFromDate = referenceDateFilter?.conditions?.find(
            (condition: any) => condition.operator === 'GreaterThanOrEqual'
          )?.value;
          dateRangeSlicerToDate = referenceDateFilter?.conditions?.find(
            (condition: any) => condition.operator === 'LessThan'
          )?.value;
        }
      }

      // Fallback to export data if slicer date range is not available
      const dateRangeItem = exportData.find(item => item.data.includes('reference_date'));
      const dateStrings =
        dateRangeItem?.data
          .split('\r\n')
          .filter((date: any) => DateRangeFormatter.isValidDateTime(date)) || [];

      this.referenceDateRange.value = {
        formatted_from_date: getFormattedDate(
          dateRangeSlicerFromDate,
          dateStrings[0],
          new Date(new Date().setFullYear(new Date().getFullYear() - 10))
        ),
        formatted_to_date: getFormattedDate(
          dateRangeSlicerToDate,
          dateStrings[dateStrings.length - 1],
          new Date()
        )
      };
    } catch (error) {
      Sentry.logger.warn('Error extracting date range', { log_source: 'power_bi', error });
      // Set default date range on error
      this.referenceDateRange.value = {
        formatted_from_date: DateRangeFormatter.formatDateToMMDDYYYY(
          new Date(new Date().setFullYear(new Date().getFullYear() - 10))
        ),
        formatted_to_date: DateRangeFormatter.formatDateToMMDDYYYY(new Date())
      };
    }
  }

  async getDateRange(): Promise<void> {
    try {
      await this.waitForVisualsReady();

      if (!this.report.activePage.value) throw new Error('Active page not found');

      const visuals = await this.report.activePage.value?.getVisuals();

      // Get slicer states with individual error handling
      const visualsSlicers = await Promise.all(
        visuals
          .filter(v => v.type === 'slicer')
          .map(async v => {
            try {
              return await v.getSlicerState();
            } catch (error) {
              Sentry.logger.warn('Could not get slicer state for visual', {
                log_source: 'power_bi',
                error
              });
              return { filters: [] };
            }
          })
      );

      const exportData = await this.getExportDataFromVisuals(visuals);
      this.extractDateRange(visualsSlicers, exportData);
    } catch (error) {
      Sentry.logger.error('Error in getDateRange', { log_source: 'power_bi', error });
      // Set default date range on error
      this.referenceDateRange.value = {
        formatted_from_date: DateRangeFormatter.formatDateToMMDDYYYY(
          new Date(new Date().setFullYear(new Date().getFullYear() - 10))
        ),
        formatted_to_date: DateRangeFormatter.formatDateToMMDDYYYY(new Date())
      };
    }
  }

  // Helper method to extract and aggregate filters
  private async extractFilters(): Promise<PowerBIFilterArray> {
    let aggregatedFilters: PowerBIFilterArray = [];
    try {
      await this.waitForVisualsReady();

      // Fetch report and active page filters asynchronously
      const reportFilters = await this.report.report.value?.getFilters();

      if (reportFilters) {
        aggregatedFilters = aggregatedFilters.concat(
          reportFilters as unknown as PowerBIFilterArray
        );
      }

      if (!this.report.activePage.value) throw new Error('Active page not found');

      const activePageFilters = await this.report.activePage.value?.getFilters();

      if (activePageFilters) {
        aggregatedFilters = aggregatedFilters.concat(
          activePageFilters as unknown as PowerBIFilterArray
        );
      }

      // Get visuals with error handling
      const visuals = await this.report.activePage.value?.getVisuals();

      // Process slicers with individual error handling
      const visualsSlicers = await Promise.all(
        (visuals || [])
          .filter(visual => visual.type === 'slicer')
          .map(async visual => {
            try {
              return await visual.getSlicerState();
            } catch (error) {
              Sentry.logger.warn('Could not get slicer state for visual', {
                log_source: 'power_bi',
                visualName: visual.name,
                error
              });
              return { filters: [] };
            }
          })
      );

      // Add slicer filters that contain valid data
      visualsSlicers.forEach(slicerState => {
        if (slicerState && slicerState.filters) {
          aggregatedFilters = aggregatedFilters.concat(
            slicerState.filters as unknown as PowerBIFilterArray
          );
        }
      });
    } catch (error) {
      Sentry.logger.error('Error retrieving filters', { log_source: 'power_bi', error });
      // Return empty array instead of throwing to make the function more resilient
    }
    return aggregatedFilters.flat().filter(Boolean);
  }

  // Method to map filters from PowerBI format
  remapPowerBIFilterArray(payload: PowerBIFilterArray): { column: string; values: any[] }[] {
    const remappedFilters: { column: string; values: any[] }[] = [];

    payload.forEach(itemOrGroup => {
      if (Array.isArray(itemOrGroup)) {
        itemOrGroup.forEach(item => {
          if (item.operator !== 'NotIn') {
            remappedFilters.push({
              column: item?.target?.column,
              values: item.values
            });
          }
        });
      } else {
        if ('target' in itemOrGroup) {
          const { column } = itemOrGroup.target;
          if ('operator' in itemOrGroup && itemOrGroup.operator !== 'NotIn') {
            remappedFilters.push({ column, values: itemOrGroup.values || [] });
          }
        }
      }
    });

    return remappedFilters
      .filter(({ column }) => column) // Filter out items without a column
      .filter(({ values }) => (Array.isArray(values) ? values.length > 0 : values)); // Filter out items without values
  }

  // Method to get all filters and update the internal filter state
  async getAllFilters(): Promise<{ column: string; values: any[] }[]> {
    const filters = await this.extractFilters();
    const remappedFilters = this.remapPowerBIFilterArray(filters);
    this.filters.value = remappedFilters;
    return remappedFilters;
  }

  // Implementing the drillThrough function as a method of the class
  async drillThrough() {
    Sentry.logger.info('Starting drill through process...', { log_source: 'power_bi' });

    try {
      // Wait for visuals to be ready before proceeding
      if (!this.report.visualsReady.value) {
        Sentry.logger.info('Visuals not yet ready, waiting for initialization...', {
          log_source: 'power_bi'
        });
        await this.waitForVisualsReady();
      }

      const allFilters = await this.getAllFilters();
      if (!allFilters || allFilters.length === 0) {
        Sentry.logger.warn('No filters found for drill through', { log_source: 'power_bi' });
        return;
      }

      // Create the filtered dimensions object
      const filteredDimensions = this.createObjectFromArray(
        allFilters.map(({ column, values }) => [this.getMxBNameFromPowerBIName(column), values])
      );

      await this.getDateRange();

      // Push the route for drill through, including the filtered dimensions and selected data
      router.push({
        path: `/analysis/${this.report.activeViewName.value}`,
        query: {
          ...filteredDimensions,
          ...this.report.selectedDataObject.value,
          is_drilling_through: 'yes',
          is_drilling_from_pbi: 'yes',
          referenceDateFrom: this.referenceDateRange.value.formatted_from_date,
          referenceDateTo: this.referenceDateRange.value.formatted_to_date
        }
      });
    } catch (error) {
      Sentry.logger.error('Error during drill through', { log_source: 'power_bi', error });
      // Handle the error gracefully, maybe show a notification to the user
    }
  }

  // Get the MxB name from the PowerBI name
  private getMxBNameFromPowerBIName(payload: string): string {
    const reportDefinitionData = this.report.reportDefinition.value;
    if (!reportDefinitionData) return payload;

    const MxBName = reportDefinitionData
      .filter(({ viewName }) => viewName === this.report.activeViewName.value)
      .map(({ targets }) =>
        targets.map(
          ({ dimensions }) =>
            dimensions.find(({ powerBiName }) => powerBiName === payload)?.mxbViewName
        )
      )
      .flat()
      .flat()[0];

    return MxBName || payload;
  }

  private createObjectFromArray(arrayOfArrays: Array<[string, any]>) {
    const obj: Record<string, any> = {};
    arrayOfArrays.forEach(([key, value]) => {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) {
        obj[key] = value;
      } else {
        if (!Array.isArray(obj[key])) {
          obj[key] = [obj[key]];
        }
        obj[key].push(value);
      }
    });
    return obj;
  }
}
